import { collectionsConfig } from 'lib/config'
import { deserializePlaceV0, PlaceV0, serializeCompactV0 } from 'lib/place'
import { isHereId } from 'lib/place/util'
import { createLookupRequestParams } from 'map/services'
import { catchError, map, Observable, of, switchMap, zip } from 'rxjs'

import { DataItem as CollectionResponseDataItem, httpGetCollections } from '../../api/collections'
import { lookup, Response as LookupPlacesResponse } from '../../api/lookup'
import {
  Data as PlaceResponseData,
  DataItem as PlaceResponseDataItem,
  httpGetCollectionPlaces,
  Response as PlacesResponse,
} from '../../api/places'
import {
  Collection,
  CollectionMap,
  mapToCollection,
  mapToPlace,
  mapToPlaceV0,
  Place,
} from '../../model'

export type CollectionEntry = [string, Collection]
export type PlaceEntry = [string, Place]

export interface AuxiliaryData {
  pageToken?: string
  total?: number
  foreignId?: string
  count?: number
}

export interface EntriesPayload<EntryType> {
  data: AuxiliaryData
  entries: EntryType[]
}

export function createCollectionEntries$(
  userAccessToken: string,
  creatorId: string,
  pageToken: string | undefined
): Observable<EntriesPayload<CollectionEntry>> {
  return httpGetCollections(userAccessToken, creatorId, pageToken).pipe(
    switchMap((response) => {
      const collectionEntries: CollectionEntry[] =
        response !== undefined && response.data !== undefined
          ? response.data
              .filter((responseItem) => !isCollectionDeleted(responseItem))
              .map((responseItem) => [responseItem.id, mapToCollection(responseItem)])
          : []

      return of({
        data: {
          count: response.count,
          pageToken: response.pageToken,
          total: response.total,
        },
        entries: collectionEntries,
      })
    })
  )
}

export function createCollectionPlaceEntries$(
  collectionId: string,
  accessToken: string,
  lang: string,
  pageToken?: string,
  accumulatedResponseData?: PlaceResponseData,
  accCallIndex = 0
): Observable<PlaceEntry[]> {
  return httpGetCollectionPlaces(
    accessToken,
    collectionId,
    pageToken,
    accCallIndex * collectionsConfig.placesRequestDelay // multiply delay time, since all the request are called with milliseconds difference.
  ).pipe(
    switchMap((placesResponse: PlacesResponse): Observable<PlaceEntry[]> => {
      const acc = accumulatedResponseData !== undefined ? accumulatedResponseData : []
      const placesResponseData = [...acc, ...placesResponse.data]

      if (placesResponse.pageToken !== undefined) {
        return createCollectionPlaceEntries$(
          collectionId,
          accessToken,
          lang,
          placesResponse.pageToken,
          placesResponseData,
          accCallIndex + 1
        )
      }

      if (placesResponseData.length === 0) {
        return of([])
      }

      return zip(
        ...placesResponseData
          .filter((place) => !isPlaceDeleted(place))
          .map((scbePlace) => {
            const deserializedData =
              scbePlace.placesId !== undefined && !isHereId(scbePlace.placesId)
                ? deserializePlaceId(scbePlace.placesId)
                : undefined

            const intrinsicId =
              deserializedData !== undefined
                ? deserializedData.id
                : scbePlace.placesId !== undefined && isHereId(scbePlace.placesId)
                ? scbePlace.placesId
                : scbePlace.placesForeignId !== undefined && isHereId(scbePlace.placesForeignId)
                ? scbePlace.placesForeignId
                : undefined

            if (intrinsicId !== undefined) {
              return lookup(createLookupRequestParams(intrinsicId, lang)).pipe(
                map((lookupResponse: LookupPlacesResponse) => {
                  const serializedPlaceV0: string =
                    scbePlace.placesId !== undefined && deserializedData !== undefined
                      ? removeSerializationVersion(scbePlace.placesId)
                      : serializeCompactV0(mapToPlaceV0(scbePlace, intrinsicId, lookupResponse))

                  return [
                    scbePlace.id,
                    mapToPlace(
                      scbePlace,
                      serializedPlaceV0,
                      intrinsicId,
                      deserializedData,
                      lookupResponse
                    ),
                  ] as PlaceEntry
                }),
                catchError((_) => {
                  const serializedPlaceV0: string =
                    scbePlace.placesId !== undefined && deserializedData !== undefined
                      ? removeSerializationVersion(scbePlace.placesId)
                      : serializeCompactV0(mapToPlaceV0(scbePlace, intrinsicId))

                  return of([
                    scbePlace.id,
                    mapToPlace(scbePlace, serializedPlaceV0, intrinsicId, deserializedData),
                  ] as PlaceEntry)
                })
              )
            }

            const serializedPlaceV0: string =
              scbePlace.placesId !== undefined && deserializedData !== undefined
                ? removeSerializationVersion(scbePlace.placesId)
                : serializeCompactV0(mapToPlaceV0(scbePlace, intrinsicId))

            return of([
              scbePlace.id,
              mapToPlace(scbePlace, serializedPlaceV0, intrinsicId, deserializedData),
            ] as PlaceEntry)
          })
      )
    })
  )
}

export function removeSerializationVersion(placesId: string): string {
  return placesId.replace('WSID-v0-', '')
}

export function deserializePlaceId(placeId: string): PlaceV0 | undefined {
  try {
    return deserializePlaceV0(placeId)
  } catch (error) {
    console.warn('Unable to deserialize id ', error)
    return undefined
  }
}

export function isReFetchingTheFirstPage(
  pageToken: string | undefined,
  collections: CollectionMap
): boolean {
  return pageToken === undefined && Object.keys(collections).length > 0
}

export function isCollectionDeleted(responseItem: CollectionResponseDataItem): boolean {
  return responseItem.deleted === true
}

export function isPlaceDeleted(responseItem: PlaceResponseDataItem): boolean {
  return responseItem.deleted === true
}
